import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '@/lib/cn';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

const sizes = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-2xl',
} as const;

/** Modal con backdrop, cierre con Escape y bloqueo de scroll del body. */
export function Modal({ open, onClose, title, description, children, footer, size = 'md' }: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center lateral:items-center lateral:p-4">
      <div
        className="mac-velo absolute inset-0 animate-fade-in"
        onClick={onClose}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        className={cn(
          // 21-oct, macOS 27: hoja en vidrio. Computadora: diálogo centrado de radio 20. Teléfono:
          // de borde a borde, pegada abajo, esquinas de 24 arriba, sube desde abajo.
          'mac-hoja relative w-full rounded-t-3xl pb-[env(safe-area-inset-bottom)] text-slate-900 animate-subir ' +
            'dark:text-slate-100 lateral:rounded-2xl lateral:pb-0 lateral:animate-scale-in',
          sizes[size],
        )}
      >
        <div className="flex items-start justify-between gap-4 px-5 pb-2 pt-5">
          <div>
            {title && <h2 className="text-[17px] font-semibold leading-snug text-slate-900 dark:text-slate-100">{title}</h2>}
            {description && <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">{description}</p>}
          </div>
          <button
            onClick={onClose}
            className="relative -mr-1 -mt-1 grid h-7 w-7 shrink-0 place-items-center rounded-full bg-[var(--mac-btn)] text-slate-500 after:absolute after:-inset-2 hover:bg-[var(--mac-btn-pulsado)] dark:text-slate-300 lateral:h-6 lateral:w-6 lateral:after:hidden"
            aria-label="Cerrar"
          >
            <X className="h-3.5 w-3.5" strokeWidth={2.25} />
          </button>
        </div>
        <div className="max-h-[70vh] overflow-y-auto overscroll-contain px-5 py-3">{children}</div>
        {footer && (
          <div className="flex flex-wrap items-center justify-end gap-2 px-5 pb-5 pt-3">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
